import { parseISO, subDays, isBefore, isAfter, startOfDay } from 'date-fns';

/**
 * Checks if a given expiration date has already passed.
 *
 * @param {string} expirationDay - Expiration date in ISO format (yyyy-MM-dd).
 * @returns {boolean} `true` if the date has expired, otherwise `false`.
 *
 * @example
 * isExpired('2025-01-01'); // depends on today's date
 */
export const isExpired = (expirationDay) => {
  if (!expirationDay) return false;
  const today = startOfDay(new Date());
  const expirationDate = startOfDay(parseISO(expirationDay));
  return isBefore(expirationDate, today);
};

/**
 * Checks if a given expiration date is within a specified
 * number of days from today (i.e. "expiring soon").
 *
 * @param {string} expirationDay - Expiration date in ISO format (yyyy-MM-dd).
 * @param {number} [expiringSoonDays=14] - Threshold in days before expiration to be considered "soon".
 * @returns {boolean} `true` if the date is within the expiring window and not expired yet, otherwise `false`.
 *
 * @example
 * isExpiringSoon('2025-12-01', 14); // true if today is within 14 days before Dec 1, 2025
 */
export const isExpiringSoon = (expirationDay, expiringSoonDays = 14) => {
  if (!expirationDay) return false;
  const today = startOfDay(new Date());
  const expirationDate = startOfDay(parseISO(expirationDay));
  const limitDate = subDays(expirationDate, expiringSoonDays);

  return isAfter(today, limitDate) && !isBefore(expirationDate, today);
};